import { useCallback, useEffect, useRef, useState } from "react";
import { FileUp, RefreshCw, RotateCcw, Trash2, UploadCloud } from "lucide-react";
import { adminApi, errText } from "./adminApi";

const fmtTs = (ts) => ts ? new Date(ts * 1000).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "—";

export default function AdminPapers() {
  const [papers, setPapers] = useState(null);
  const [topics, setTopics] = useState([]);
  const [files, setFiles] = useState([]);
  const [name, setName] = useState("");
  const [topicId, setTopicId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [solFile, setSolFile] = useState(null);
  const fileRef = useRef(null);
  const solRef = useRef(null);

  const load = useCallback(async () => {
    const [{ data: p }, { data: t }] = await Promise.all([adminApi.get("/admin/papers"), adminApi.get("/admin/test/topics")]);
    setPapers(p.papers);
    setTopics(t.topics);
    return p.papers;
  }, []);

  useEffect(() => { load().catch(() => setError("Could not load papers.")); }, [load]);

  // poll while any paper is processing
  useEffect(() => {
    if (!papers?.some((p) => p.status === "processing")) return undefined;
    const t = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [papers, load]);

  const upload = async () => {
    if (!files.length) { setError("Choose one or more PDF files first."); return; }
    setUploading(true);
    setError("");
    setNotice("");
    let ok = 0;
    const rejected = [];
    for (const f of files) {
      try {
        const form = new FormData();
        form.append("file", f);
        form.append("name", files.length === 1 && name.trim() ? name.trim() : f.name.replace(/\.pdf$/i, ""));
        form.append("topic_id", topicId);
        await adminApi.post("/admin/papers", form, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) { rejected.push(`${f.name} — ${errText(err)}`); }
    }
    if (ok) setNotice(`${ok} PDF${ok > 1 ? "s" : ""} uploaded — extraction runs one paper at a time in the background (watch the status pills below). You can leave this page; questions appear in the bank automatically.`);
    if (rejected.length) setError(`Rejected: ${rejected.join(" · ")}`);
    setFiles([]);
    setName("");
    if (fileRef.current) fileRef.current.value = "";
    await load();
    setUploading(false);
  };

  const viewDetail = async (pid) => {
    if (expanded === pid) { setExpanded(null); return; }
    setExpanded(pid);
    setDetail(null);
    const { data } = await adminApi.get(`/admin/papers/${pid}`);
    setDetail(data);
  };

  const uploadSolutions = async (pid) => {
    if (!solFile) { setError("Choose the solutions/answer-key PDF first."); return; }
    try {
      const form = new FormData();
      form.append("file", solFile);
      const { data } = await adminApi.post(`/admin/papers/${pid}/solutions`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setNotice(`Answer key mapped onto ${data.answers_mapped} questions.`);
      setSolFile(null);
      if (solRef.current) solRef.current.value = "";
      await load();
      if (expanded === pid) viewDetail(pid);
    } catch (err) { setError(errText(err)); }
  };

  const reprocess = async (pid) => {
    await adminApi.post(`/admin/papers/${pid}/reprocess`);
    setNotice("Re-processing started.");
    await load();
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this paper and all its extracted questions?")) return;
    try {
      await adminApi.delete(`/admin/papers/${pid}`);
      await load();
    } catch (err) { setError(errText(err)); }
  };

  return (
    <div data-testid="admin-papers">
      <h4 style={{ marginBottom: 4 }}>Question Paper PDFs</h4>
      <span className="lead-sub">Upload full papers (45, 100 or more questions) — pick up to 10 PDFs at once for a whole test series. The system reads them one at a time automatically — questions, options, diagrams and answer keys — and puts everything into the bank. Anything uncertain lands in the AI Studio review queue for approve/delete.</span>
      {error && <p className="admin-error" data-testid="papers-error">{error}</p>}
      {notice && <p className="tp-success-box" style={{ background: "#123a2e", border: "1px solid #12b76a", color: "#4ade80" }}>{notice}</p>}

      <div className="admin-card" style={{ marginTop: 14 }} data-testid="paper-upload-card">
        <div className="admin-row" style={{ flexWrap: "wrap" }}>
          <input ref={fileRef} type="file" accept="application/pdf" multiple onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 10))} data-testid="paper-file" style={{ color: "#9db1c9", fontSize: 13 }} />
          <input className="admin-input" style={{ maxWidth: 220 }} placeholder="Paper name (single upload only)" value={name} onChange={(e) => setName(e.target.value)} data-testid="paper-name" disabled={files.length > 1} />
          <select className="admin-input" style={{ maxWidth: 200 }} value={topicId} onChange={(e) => setTopicId(e.target.value)} data-testid="paper-topic">
            <option value="">Topic (optional)…</option>
            {topics.map((t) => <option key={t.topic_id} value={t.topic_id}>{t.name}</option>)}
          </select>
          <button className="admin-btn" disabled={uploading || !files.length} onClick={upload} data-testid="paper-upload-btn">
            {uploading ? "Uploading…" : <><UploadCloud size={14} /> {files.length > 1 ? `Upload ${files.length} PDFs & extract` : "Upload & extract"}</>}
          </button>
        </div>
        {files.length > 0 && (
          <div className="admin-row" style={{ flexWrap: "wrap", marginTop: 10, gap: 6 }} data-testid="paper-selected-files">
            {files.map((f, i) => (
              <span className="status-pill review" key={f.name} data-testid={`paper-selected-${i}`}>{f.name} · {(f.size / 1024 / 1024).toFixed(1)} MB</span>
            ))}
            {files.length > 1 && <span className="lead-sub" style={{ alignSelf: "center" }}>each PDF becomes its own paper, named after its file</span>}
          </div>
        )}
      </div>

      {papers === null && <div className="admin-empty">Loading…</div>}
      {papers?.length === 0 && <div className="admin-empty" data-testid="papers-empty">No papers uploaded yet. Upload your first question paper PDF above.</div>}
      {papers?.map((p, i) => (
        <div className="admin-card" key={p.paper_id} data-testid={`paper-row-${i}`}>
          <div className="admin-row">
            <span className={`status-pill ${p.status === "ready" ? "approved" : p.status === "failed" ? "failed" : "review"}`} data-testid={`paper-status-${i}`}>
              {p.status === "processing" ? (p.progress || "PROCESSING…") : p.status.toUpperCase()}</span>
            <b style={{ color: "#eef4fc", flex: 1 }}>{p.name}</b>
            <span className="lead-sub">{p.topic_name} · {p.pages} pages · {fmtTs(p.created_at)}{p.scanned ? " · scanned (OCR)" : ""}</span>
          </div>
          {p.status === "ready" && <div className="admin-row" style={{ marginTop: 8 }} data-testid={`paper-stats-${i}`}>
            <span className="lead-sub">{p.extracted} questions · {p.answers_from_pdf} answers from PDF · {p.answers_ai_solved} AI-solved · {p.dupes_skipped} duplicates skipped{p.flagged_count ? ` · ${p.flagged_count} flagged` : ""}{p.missing_qnos?.length ? ` · missing qnos: ${p.missing_qnos.join(", ")}` : ""}{p.used_in_tests ? ` · used in ${p.used_in_tests} test(s)` : ""}</span>
          </div>}
          {p.status === "failed" && <p className="admin-error" style={{ marginTop: 8 }}>{p.error}</p>}
          <div className="admin-row" style={{ marginTop: 8 }}>
            <button className="admin-btn" onClick={() => viewDetail(p.paper_id)} data-testid={`paper-view-${i}`}><FileUp size={13} /> {expanded === p.paper_id ? "Hide" : "View questions"}</button>
            <input ref={solRef} type="file" accept="application/pdf" onChange={(e) => setSolFile(e.target.files[0])} style={{ color: "#9db1c9", fontSize: 12, maxWidth: 180 }} data-testid={`paper-solfile-${i}`} />
            <button className="admin-btn" disabled={!solFile} onClick={() => uploadSolutions(p.paper_id)} data-testid={`paper-solutions-${i}`}>Map answer key</button>
            <button className="admin-btn" onClick={() => reprocess(p.paper_id)} data-testid={`paper-reprocess-${i}`}><RotateCcw size={13} /> Reprocess</button>
            <button className="admin-btn" onClick={() => remove(p.paper_id)} data-testid={`paper-delete-${i}`}><Trash2 size={13} /></button>
            <button className="admin-btn" onClick={() => load()}><RefreshCw size={13} /></button>
          </div>
          {expanded === p.paper_id && (
            detail ? <div style={{ marginTop: 10, maxHeight: 420, overflowY: "auto" }} data-testid="paper-questions">
              {detail.questions.map((q, j) => (
                <div key={q.question_id} style={{ borderTop: "1px solid #22314a", padding: "8px 0" }} data-testid={`paper-q-${j}`}>
                  <div className="admin-row">
                    <span className="status-pill locked">Q{q.paper_qno}</span>
                    {q.flagged && <span className="status-pill failed">FLAGGED</span>}
                    {q.image_url && <span className="status-pill review">DIAGRAM</span>}
                    <span style={{ flex: 1, color: "#dbe6f4", fontSize: 12 }}>{q.text.slice(0, 120)}{q.text.length > 120 ? "…" : ""}</span>
                    <span className="lead-sub">Ans: {q.correct}</span>
                  </div>
                  {q.flag_reason && <p className="lead-sub" style={{ color: "#ff9e9e" }}>{q.flag_reason}</p>}
                </div>
              ))}
              {detail.questions.length === 0 && <div className="admin-empty">No questions extracted yet.</div>}
            </div> : <p className="lead-sub" style={{ marginTop: 8 }}>Loading questions…</p>)}
        </div>
      ))}
    </div>
  );
}
